/**
 * Email Validator Utility
 *
 * Provides email validation and sanitization for the auth flow.
 *
 * @module features/auth/utils/emailValidator
 * @see SPEC-005.5-auth-login-ui Requirements 1.8, 10.6
 */

/**
 * Email validation result
 */
export interface EmailValidationResult {
  isValid: boolean;
  sanitized: string;
  error?: string;
}

/**
 * Validate and sanitize email address
 *
 * Performs the following operations:
 * 1. Sanitization: trim whitespace, convert to lowercase, remove newlines
 * 2. Format validation: check for valid email format
 *
 * @param email - Raw email input
 * @returns Validation result with sanitized email
 *
 * @example
 * ```typescript
 * const result = validateEmail('  User@Example.COM  ');
 * // { isValid: true, sanitized: 'user@example.com' }
 *
 * const invalid = validateEmail('not-an-email');
 * // { isValid: false, sanitized: 'not-an-email', error: 'auth.validation.emailInvalid' }
 * ```
 */
export function validateEmail(email: string): EmailValidationResult {
  // Sanitize: remove newlines/carriage returns first (security: header injection prevention)
  let sanitized = email.replace(/[\r\n]/g, '');

  // Sanitize: trim whitespace and convert to lowercase
  sanitized = sanitized.trim().toLowerCase();

  // Check for empty
  if (!sanitized) {
    return {
      isValid: false,
      sanitized,
      error: 'auth.validation.emailRequired',
    };
  }

  // Basic email format validation
  // Pattern: local@domain.tld
  // - local: one or more non-whitespace, non-@ characters
  // - domain: one or more non-whitespace, non-@ characters
  // - tld: one or more non-whitespace, non-@ characters
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  if (!emailRegex.test(sanitized)) {
    return {
      isValid: false,
      sanitized,
      error: 'auth.validation.emailInvalid',
    };
  }

  return {
    isValid: true,
    sanitized,
  };
}
